import React from "react";
import { Link } from "react-router-dom";
import useSectionScroll from "../../hooks/useSectionScroll";

export default function Footer() {
  const goToSection = useSectionScroll();

  const navLinks = [
    { label: "Services", type: "route", path: "/services" },
    { label: "Portfolio", type: "scroll", id: "portfolio" },
    { label: "Technology", type: "scroll", id: "technology" },
    { label: "About", type: "route", path: "/about" },
    { label: "Process", type: "scroll", id: "process" },
    { label: "FAQ", type: "scroll", id: "faq" },
    { label: "Contact", type: "route", path: "/contact" },
  ];

  const services = [
    "Web Development",
    "Mobile Apps",
    "UI / UX Design",
    "Custom Software",
    "Cloud Solutions",
    "API Development",
  ];

  return (
    <footer className="overflow-hidden bg-[#1A1714] text-white">
      {/* Main Footer */}
      <div
        className="
          mx-auto
          w-full
          max-w-[1440px]
          px-5
          py-12
          sm:px-6
          sm:py-14
          lg:px-12
          lg:py-20
        "
      >
        <div
          className="
            grid
            grid-cols-2
            gap-x-5
            gap-y-10
            sm:gap-x-8
            sm:gap-y-12
            md:grid-cols-2
            lg:grid-cols-4
            lg:gap-12
          "
        >
          {/* Brand */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            <h3
              className="
                mb-4
                text-2xl
                font-bold
                sm:text-3xl
              "
            >
              Zevix{" "}
              <span className="italic text-[#C8762A]">
                Tech
              </span>
            </h3>

            <p
              className="
                max-w-md
                text-sm
                leading-6
                text-stone-400
                sm:text-base
                sm:leading-7
                lg:leading-8
              "
            >
              Building scalable software solutions, web applications,
              mobile apps and digital products for startups and businesses
              worldwide.
            </p>

            {/* Social Links */}
            <div className="mt-5 flex gap-3 sm:mt-6 sm:gap-4">
              <a
                href="#"
                aria-label="Website"
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-stone-700
                  text-stone-400
                  transition
                  duration-300
                  hover:border-[#C8762A]
                  hover:text-[#C8762A]
                "
              >
                <span className="material-symbols-outlined text-[18px]">
                  language
                </span>
              </a>

              <a
                href="mailto:zevixtech@gmail.com"
                aria-label="Email"
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-stone-700
                  text-stone-400
                  transition
                  duration-300
                  hover:border-[#C8762A]
                  hover:text-[#C8762A]
                "
              >
                <span className="material-symbols-outlined text-[18px]">
                  alternate_email
                </span>
              </a>

              <a
                href="#"
                aria-label="Business"
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-stone-700
                  text-stone-400
                  transition
                  duration-300
                  hover:border-[#C8762A]
                  hover:text-[#C8762A]
                "
              >
                <span className="material-symbols-outlined text-[18px]">
                  business
                </span>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="min-w-0">
            <h4
              className="
                mb-4
                text-base
                font-semibold
                sm:mb-5
                sm:text-lg
              "
            >
              Navigation
            </h4>

            <div className="flex flex-col gap-2.5 sm:gap-3">
              {navLinks.map((link) =>
                link.type === "route" ? (
                  <Link
                    key={link.label}
                    to={link.path}
                    className="
                      text-sm
                      text-stone-400
                      transition
                      duration-300
                      hover:text-white
                      sm:text-base
                    "
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.label}
                    type="button"
                    onClick={() => goToSection(link.id)}
                    className="
                      text-left
                      text-sm
                      text-stone-400
                      transition
                      duration-300
                      hover:text-white
                      sm:text-base
                    "
                  >
                    {link.label}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Services */}
          <div className="min-w-0">
            <h4
              className="
                mb-4
                text-base
                font-semibold
                sm:mb-5
                sm:text-lg
              "
            >
              Services
            </h4>

            <div className="flex flex-col gap-2.5 sm:gap-3">
              {services.map((service) => (
                <span
                  key={service}
                  className="
                    break-words
                    text-sm
                    leading-5
                    text-stone-400
                    sm:text-base
                  "
                >
                  {service}
                </span>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div
            className="
              col-span-2
              border-t
              border-stone-800
              pt-8
              md:col-span-1
              md:border-t-0
              md:pt-0
              lg:col-span-1
            "
          >
            <h4
              className="
                mb-4
                text-base
                font-semibold
                sm:mb-5
                sm:text-lg
              "
            >
              Contact
            </h4>

            <div
              className="
                grid
                grid-cols-1
                gap-4
                sm:grid-cols-3
                md:grid-cols-1
              "
            >
              {/* Email */}
              <div className="min-w-0">
                <p className="mb-1 text-xs text-stone-500 sm:text-sm">
                  Email
                </p>

                <a
                  href="mailto:zevixtech@gmail.com"
                  className="
                    break-all
                    text-sm
                    text-stone-300
                    transition
                    hover:text-[#C8762A]
                    sm:text-base
                  "
                >
                  zevixtech@gmail.com
                </a>
              </div>

              {/* Phone */}
              <div>
                <p className="mb-1 text-xs text-stone-500 sm:text-sm">
                  Phone
                </p>

                <a
                  href="tel:+917340200113"
                  className="
                    text-sm
                    text-stone-300
                    transition
                    hover:text-[#C8762A]
                    sm:text-base
                  "
                >
                  +91 73402 00113
                </a>
              </div>

              {/* Location */}
              <div>
                <p className="mb-1 text-xs text-stone-500 sm:text-sm">
                  Location
                </p>

                <p className="text-sm leading-6 text-stone-300 sm:text-base">
                  Jaipur, Rajasthan, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-stone-800">
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-[1440px]
            flex-col
            items-center
            justify-between
            gap-3
            px-5
            py-5
            text-center
            text-xs
            text-stone-500
            sm:px-6
            sm:text-sm
            md:flex-row
            md:gap-4
            md:py-6
            md:text-left
            lg:px-12
          "
        >
          <p>© 2026 Zevix Tech. All rights reserved.</p>

          <div
            className="
              flex
              flex-wrap
              items-center
              justify-center
              gap-x-5
              gap-y-2
              sm:gap-x-6
            "
          >
            <a
              href="#"
              className="transition duration-300 hover:text-white"
            >
              Privacy Policy
            </a>

            <a
              href="#"
              className="transition duration-300 hover:text-white"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}